import React, { useState, useEffect } from "react";
import { WorkflowIcon, ChevronDown, CheckCheckIcon, PlusIcon } from "lucide-react";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useNotification } from "../../../hooks";
import { useGetChains } from "../../../useQuery";

const AddChainModal = ({ open, onClose, onAddChain, addingToSuite }) => {
  const notify = useNotification();
  const [chains, setChains] = useState([]);
  const [expandedChains, setExpandedChains] = useState({});

  const [selectedChainIds, setSelectedChainIds] = useState(new Set());

  const { mutate: fetchChains, isPending: loadingChains } = useGetChains({
    onSuccess: (data) => setChains(data || []),
    onError: () => notify("Error fetching chains", "error"),
  });

  useEffect(() => {
    if (open) {
      fetchChains();
      setExpandedChains({});
      setSelectedChainIds(new Set());
    }
  }, [open]);

  const handleChainToggle = (chainId) => {
    setExpandedChains((prev) => ({ ...prev, [chainId]: !prev[chainId] }));
  };

  const toggleChain = (chainId) => {
    setSelectedChainIds((prev) => {
      const next = new Set(prev);
      next.has(chainId) ? next.delete(chainId) : next.add(chainId);
      return next;
    });
  };

  const allSelected = chains.length > 0 && chains.every((c) => selectedChainIds.has(c.id));
  const partialSelected = chains.some((c) => selectedChainIds.has(c.id)) && !allSelected;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedChainIds(new Set());
    } else {
      setSelectedChainIds(new Set(chains.map((c) => c.id)));
    }
  };

  const selectedCount = selectedChainIds.size;

  const handleAdd = () => {
    selectedChainIds.forEach((chainId) => onAddChain(chainId));
    setSelectedChainIds(new Set());
  };

  const getMethodColor = (method) => {
    switch (method?.toUpperCase()) {
      case "GET":
        return { textColor: "text-green-700", bgColor: "#f0f9ff", borderColor: "#bae6fd" };
      case "POST":
        return { textColor: "text-blue-700", bgColor: "#eff6ff", borderColor: "#dbeafe" };
      case "PUT":
        return { textColor: "text-orange-700", bgColor: "#fff7ed", borderColor: "#fed7aa" };
      case "DELETE":
        return { textColor: "text-red-700", bgColor: "#fef2f2", borderColor: "#fecaca" };
      case "PATCH":
        return { textColor: "text-purple-700", bgColor: "#faf5ff", borderColor: "#e9d5ff" };
      default:
        return { textColor: "text-gray-700", bgColor: "#f9fafb", borderColor: "#d1d5db" };
    }
  };

  const formatInputParameters = (params) => {
    if (!params || typeof params !== "object") return "";
    return Object.entries(params)
      .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
      .join(", ");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50 shrink-0">
        <span className="font-apple text-lg font-medium text-gray-800 flex items-center gap-2">
          Add Chains
          <WorkflowIcon size={20} />
        </span>

        {chains.length > 0 && (
          <label className="flex items-center gap-2 cursor-pointer select-none group">
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = partialSelected;
              }}
              onChange={toggleSelectAll}
              className="w-4 h-4 accent-purple-600 cursor-pointer"
            />
            <span className="text-sm text-gray-500 group-hover:text-purple-600 transition-colors flex items-center gap-1">
              <CheckCheckIcon size={14} />
              Select All
            </span>
          </label>
        )}
      </div>

      <DialogContent
        sx={{
          padding: "0",
          backgroundColor: "#ffffff",
          overflowY: "auto",
          flex: 1,
        }}
      >
        <div className="font-apple p-4">
          {loadingChains ? (
            <div className="text-center py-8 text-sm text-gray-500">Loading chains...</div>
          ) : chains.length === 0 ? (
            <div className="text-center py-12">
              <WorkflowIcon size={48} className="text-gray-300 mx-auto mb-4" />
              <div className="text-sm text-gray-500">No chains found</div>
            </div>
          ) : (
            <div className="space-y-2">
              {chains.map((chain) => {
                const steps = chain.steps ? JSON.parse(chain.steps) : [];
                const isOpen = expandedChains[chain.id];
                const isChecked = selectedChainIds.has(chain.id);

                return (
                  <div
                    key={chain.id}
                    className={`rounded-md border shadow-sm transition-all duration-150
                      ${isChecked ? "border-purple-200 bg-purple-50/40" : "border-gray-200 bg-white"}`}
                  >
                    <div className="flex items-center px-4 py-3 gap-3">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleChain(chain.id)}
                        className="w-4 h-4 accent-purple-600 cursor-pointer shrink-0"
                      />

                      <button
                        onClick={() => handleChainToggle(chain.id)}
                        className="flex items-center flex-1 text-left gap-3 cursor-pointer"
                      >
                        <ChevronDown
                          size={16}
                          className={`text-gray-400 transition-transform duration-200 shrink-0 ${isOpen ? "rotate-180" : ""}`}
                        />
                        <WorkflowIcon size={16} className="text-purple-600 shrink-0" />
                        <span className="text-sm text-gray-800 flex-1">{chain.name}</span>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full shrink-0">
                          {steps.length} steps
                        </span>
                      </button>
                    </div>

                    {isOpen && (
                      <div className="border-t border-gray-100 px-4 py-3">
                        {chain.description && (
                          <div className="mb-3 p-3 bg-purple-50 rounded border border-purple-200 text-sm text-purple-800">
                            <strong>Description:</strong> {chain.description}
                            {chain.validation_criteria && (
                              <div className="text-purple-700 mt-1">
                                <strong>Validation:</strong> {chain.validation_criteria}
                              </div>
                            )}
                          </div>
                        )}

                        {steps.length === 0 ? (
                          <div className="text-center py-4 text-gray-400 text-sm">
                            No steps defined for this chain
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {steps.map((step, index) => {
                              const ms = getMethodColor(step.http_method);
                              return (
                                <div
                                  key={step.step_number || index}
                                  className="flex items-center py-2 px-3 bg-gray-50 rounded border border-gray-100 hover:bg-gray-100 transition-colors"
                                >
                                  <div className="flex items-center justify-center w-6 h-6 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold mr-3 shrink-0">
                                    {step.step_number}
                                  </div>
                                  <span
                                    className={`text-xs px-2 py-1 rounded mr-3 min-w-[60px] text-center border ${ms.textColor}`}
                                    style={{
                                      backgroundColor: ms.bgColor,
                                      borderColor: ms.borderColor,
                                    }}
                                  >
                                    {step.http_method}
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm text-gray-700 font-medium">
                                      {step.endpoint}
                                    </div>
                                    <div className="text-xs text-gray-500">{step.test_case}</div>
                                    <div className="flex items-center gap-4 mt-0.5 text-xs text-gray-400">
                                      <span>Status: {step.expected_status_code}</span>
                                      <span>Result: {step.expected_result}</span>
                                      {step.input_parameters &&
                                        Object.keys(step.input_parameters).length > 0 && (
                                          <span className="truncate max-w-xs">
                                            Params: {formatInputParameters(step.input_parameters)}
                                          </span>
                                        )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>

      <DialogActions
        sx={{
          padding: "16px 24px",
          borderTop: "1px solid #e0e0e0",
          backgroundColor: "#fafafa",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <span className="font-apple text-sm">
          {selectedCount > 0 ? (
            <span className="text-purple-600 font-medium">
              {selectedCount} chain{selectedCount !== 1 ? "s" : ""} selected
            </span>
          ) : (
            <span className="text-gray-400">Select chains to add</span>
          )}
        </span>

        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 font-apple text-sm text-white bg-red-500 hover:bg-red-600 rounded transition-all duration-200 cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleAdd}
            disabled={selectedCount === 0 || addingToSuite}
            className={`flex items-center gap-2 px-5 py-2 font-apple text-sm text-white rounded transition-all duration-200
              ${
                selectedCount === 0 || addingToSuite
                  ? "bg-purple-300 cursor-not-allowed"
                  : "bg-purple-600 hover:bg-purple-700 cursor-pointer"
              }`}
          >
            <PlusIcon size={14} />
            {addingToSuite ? "Adding..." : `Add${selectedCount > 0 ? ` (${selectedCount})` : ""}`}
          </button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default AddChainModal;
